import React, { FC, useEffect, useRef, useState } from "react";
import classNames from "classnames";

import { Currency, CurrencyType } from "../../../types/InvoiceTypes";
import { CURRENCIES } from "../../../constants";
import { CurrencyOption } from "./CurrencyOption";
import { Button } from "../Button";
import Icon from "../Icon";

type Props = {
  name: string;
  value: Currency;
  onChange: (value: Currency) => void;
  readonly?: boolean;
  currencyType?: CurrencyType;
  dataTest?: string;
  allowedCurrencies?: Currency[];
  className?: string;
};

export const CurrencyDropdown: FC<Props> = ({
  name: instanceName,
  value,
  onChange,
  dataTest,
  readonly,
  currencyType = CurrencyType.FIAT,
  allowedCurrencies = [],
  className,
}) => {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Detect clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  const handleExpand = () => setExpanded((current) => !current);

  const handleSelect = (selectedCurrency: Currency) => {
    onChange(selectedCurrency);
    setExpanded(false);
  };

  return (
    <div
      className={classNames("relative", className)}
      data-test={dataTest}
      ref={ref}
    >
      <div className="flex">
        <Button
          className="border-0 bg-transparent hover:bg-transparent flex !p-0"
          contentClassName="flex"
          variant={Button.variant.TERTIARY}
          size={Button.size.SMALL}
          onClick={!readonly ? handleExpand : undefined}
          dataTest={`${dataTest}-button`}
        >
          <CurrencyOption currency={value} />

          {(!readonly || true) && <Icon icon="Chevron-down" size={24} />}
        </Button>
      </div>

      {!readonly && expanded && (
        <div
          className="absolute top-10 left-0 flex flex-wrap bg-white rounded-md shadow-md z-10"
          data-test={`${dataTest}-dropdown`}
        >
          {CURRENCIES.filter(
            ({ value, type }) =>
              (!currencyType || type === currencyType) &&
              allowedCurrencies.includes(value)
          ).map(({ value: symbol }) => (
            <CurrencyOption
              className={classNames(
                "px-2.5 py-2 hover:bg-violet-50 first:rounded-t-md last:rounded-b-md w-full",
                {
                  "bg-violet-100": value === symbol,
                }
              )}
              currency={symbol}
              key={`${instanceName}-${symbol}`}
              dataTest={`${dataTest}-option-${symbol}`}
              onClick={() => handleSelect(symbol)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
